var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { Box, IconButton, Divider as MuiDivider, styled, Tab, Tabs, Tooltip, Typography, } from '@mui/material';
import React from 'react';
import { globalHeaderHeightNormal, globalHeaderHeightSmall } from '../GlobalHeader/constants';
import { globalInfoHeight } from '../GlobalInfo/constants';
import { globalMenuWidthFull, globalMenuWidthSmall } from '../GlobalMenu/constants';
import { useCommonWebTranslation, useHoverSupported, useSmallDevice } from '../hooks';
import { themeColors } from '../JuvareTheme';
import { GlobalTabDivider } from './components/GlobalTabDivider';
import { GlobalTabLabel } from './components/GlobalTabLabel';
import { GlobalTabsMenu } from './components/GlobalTabsMenu';
import { ReorderDragOverlay } from './components/ReorderDragOverlay';
import { globalTabsHeight } from './constants';
import { useKeyboardReorder } from './hooks/useKeyboardReorder';
import { usePointerDragReorder } from './hooks/usePointerDragReorder';
import { tabStyle } from './utils/tabStyle';
/**
 * Renders the global tabs bar used for navigating and managing multiple dynamic tabs in the application.
 *
 * @param {GlobalTabsBarProps} props - The properties used to configure the global tabs bar.
 * @param {GlobalTab[]} props.tabs - Array of open tabs currently rendered.
 * @param {string} props.selectedTabPath  - Path of the currently active/selected tab.
 * @param {(tab: GlobalTab) => void} props.onOpenTab - Callback when a tab is selected or opened.
 * @param {(tab: GlobalTab, nextSelectedTab?: GlobalTab) => void} props.onCloseTab - Callback when a tab is closed.
 * @param {() => void} props.onCloseAllTabs - Callback to close all currently open tabs.
 * @param {(tabs: GlobalTab[]) => void} props.onChangeTabs - Optional callback when tabs are reordered.
 * @param {boolean} props.menuOpen - Whether the global menu is currently open.
 * @param {'expand-on-hover' | 'icon' | 'icon-and-label'} props.menuMode - Display mode of the global menu.
 * @param {boolean} props.infoOpen - Whether the global info banner is visible.
 * @param {GlobalHeaderItem[]} props.additionalActions - Optional additional action icons/buttons to be rendered at the end of the tab bar.
 * @param {SxProps<Theme>} props.sx - Optional styling overrides.
 *
 * @returns {JSX.Element} The rendered global tabs bar including tab navigation, additional actions, and drag overlays.
 */
export const GlobalTabsBar = (props) => {
    var _a;
    const { t } = useCommonWebTranslation();
    const rootRef = React.useRef(null);
    const [hoveredTabPath, setHoveredTabPath] = React.useState(undefined);
    const handleChange = (_event, tabPath) => {
        const tab = props.tabs.find((tab) => tab.path === tabPath);
        if (tab) {
            props.onOpenTab(tab);
        }
    };
    const smallDevice = useSmallDevice();
    const hoverSupported = useHoverSupported() && !smallDevice;
    const { showKeyboardTooltip, setShowKeyboardTooltip, handleKeyDown } = useKeyboardReorder(props, rootRef);
    const { dragPoint, dragOffset, dragMoveToIndex, dragMoveToRect, draggedTab, pointerDragEvents } = usePointerDragReorder(props, rootRef);
    const handleClose = (tab) => {
        var _a;
        const selectedTabIndex = props.tabs.findIndex((t) => t.path === props.selectedTabPath);
        const selectedTab = selectedTabIndex !== -1 ? props.tabs[selectedTabIndex] : undefined;
        let nextSelectedTab = selectedTab;
        if (tab === selectedTab) {
            nextSelectedTab = (_a = props.tabs[selectedTabIndex + 1]) !== null && _a !== void 0 ? _a : props.tabs[selectedTabIndex - 1];
        }
        props.onCloseTab(tab, nextSelectedTab);
    };
    const setTabBackground = React.useCallback((tab) => {
        return hoveredTabPath === tab.path
            ? themeColors.accent.blue.alpha
            : props.selectedTabPath === tab.path
                ? themeColors.primary.surface
                : undefined;
    }, [hoveredTabPath, props.selectedTabPath]);
    return (React.createElement(Box, { ref: rootRef, display: 'flex', justifyContent: 'space-between', sx: [
            {
                position: 'fixed',
                top: (smallDevice ? globalHeaderHeightSmall : globalHeaderHeightNormal) + (props.infoOpen ? globalInfoHeight : 0) + 'px',
                right: 0,
                left: 0,
                height: globalTabsHeight,
                paddingLeft: 12 +
                    (props.menuOpen && hoverSupported && props.menuMode !== 'icon-and-label' ? globalMenuWidthSmall : 0) +
                    (props.menuOpen && hoverSupported && props.menuMode === 'icon-and-label' ? globalMenuWidthFull : 0) +
                    'px',
                paddingRight: '4px',
                display: 'flex',
                overflow: 'hidden',
                alignItems: 'center',
                gap: 1,
                backgroundColor: themeColors.surface.primary,
                boxShadow: (theme) => theme.shadows[2],
                color: themeColors.text.link,
                transition: (theme) => theme.transitions.create('padding-left') + ',' + theme.transitions.create('margin-right', { duration: 400 }),
                zIndex: (theme) => theme.zIndex.appBar - 4,
                '@media print': {
                    display: 'none',
                },
            },
            ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
        ], "data-test": 'tabs-bar' },
        props.tabs.length > 0 && (React.createElement(Tabs, { variant: 'scrollable', scrollButtons: true, allowScrollButtonsMobile: true, value: (_a = props.selectedTabPath) !== null && _a !== void 0 ? _a : false, onChange: handleChange, onKeyDown: handleKeyDown, slotProps: {
                indicator: { sx: { opacity: 0 } },
                scrollButtons: {
                    sx: {
                        height: '28px',
                        width: '28px',
                        border: themeColors.border.main,
                        borderStyle: 'solid',
                        borderWidth: '2px',
                        borderRadius: '28px',
                        color: themeColors.text.link,
                    },
                    slotProps: {
                        startScrollButtonIcon: { className: 'juvareGlobalTabScrollStart' },
                        endScrollButtonIcon: { className: 'juvareGlobalTabScrollEnd' },
                    },
                },
            }, sx: {
                width: '100%',
                minHeight: 'initial',
                maxHeight: '46px',
                transition: 'opacity 0.5 ease',
                alignItems: 'center',
                '& button': { minHeight: '31px', transition: '0.3s' },
                '& button:hover': { backgroundColor: themeColors.accent.blue.alpha },
                '& button.Mui-disabled': { opacity: 1 },
            } },
            draggedTab && React.createElement(GlobalTabDivider, { index: 0, movePlaceholder: dragMoveToIndex === 0 }),
            props.tabs.map((tab, index) => [
                React.createElement(StyledTab, Object.assign({ key: tab.path, value: tab.path }, (hoverSupported ? pointerDragEvents : {}), { onFocusVisible: () => {
                        if (props.selectedTabPath === tab.path) {
                            setShowKeyboardTooltip(true);
                        }
                    }, onBlur: () => setShowKeyboardTooltip(false), onMouseEnter: () => {
                        setHoveredTabPath(tab.path);
                    }, onMouseLeave: () => setHoveredTabPath(undefined), label: React.createElement(GlobalTabLabel, { tabs: props.tabs, selectedTabPath: props.selectedTabPath, tab: tab, showKeyboardTooltip: showKeyboardTooltip && !dragPoint }), onKeyDown: (event) => {
                        if (event.key === 'Enter') {
                            setShowKeyboardTooltip(true);
                        }
                    }, sx: {
                        opacity: draggedTab === tab ? 0 : undefined,
                        backgroundColor: setTabBackground(tab),
                        transition: '0.3s',
                        borderRadius: (theme) => `${theme.spacing(0.5)} 0 0 ${theme.spacing(0.5)}`,
                    }, "data-tab-path": tab.path, "data-test": tab['data-test'] })),
                React.createElement(StyledIconButton, { role: 'tab', key: `${tab.path}-close`, "aria-label": `${t('globalTabsBar.close')} ${tab.label}`, onClick: (e) => {
                        e.stopPropagation();
                        handleClose(tab);
                    }, onMouseEnter: () => setHoveredTabPath(tab.path), onMouseLeave: () => setHoveredTabPath(undefined), "data-test": 'close', "data-tab-close-button": 'true', tabIndex: props.selectedTabPath === tab.path ? 0 : -1, sx: {
                        opacity: draggedTab === tab ? 0 : undefined,
                        backgroundColor: setTabBackground(tab),
                        borderRadius: (theme) => `0 ${theme.spacing(0.5)} ${theme.spacing(0.5)} 0`,
                    } },
                    React.createElement(CancelOutlinedIcon, { fontSize: 'small', sx: { color: themeColors.text.link } })),
                React.createElement(GlobalTabDivider, { index: index + 1, key: 'tabDivider', movePlaceholder: dragMoveToIndex === index + 1 }),
            ]))),
        props.tabs.length <= 0 && (React.createElement(Box, { display: 'flex', width: '100%', justifyContent: 'center' },
            React.createElement(Typography, { variant: 'subtitle2' }, t('globalTabsBar.newlyOpenedTabsWillAppearHere')))),
        props.tabs.length > 0 && (React.createElement(Box, { display: 'flex' },
            React.createElement(Divider, { orientation: 'vertical' }),
            props.additionalActions &&
                props.additionalActions.map((_a) => {
                    var { onClick } = _a, item = __rest(_a, ["onClick"]);
                    return (React.createElement(React.Fragment, { key: item.label }, item.render ? (item.render()) : (React.createElement(Tooltip, { title: item.label },
                        React.createElement(IconButton, { size: 'medium', sx: { color: themeColors.text.link }, "aria-label": item.label, onClick: onClick
                                ? (event) => {
                                    event.preventDefault();
                                    onClick();
                                }
                                : undefined, "data-test": item['data-test'] }, item.icon)))));
                }),
            React.createElement(GlobalTabsMenu, { tabs: props.tabs, onOpenTab: props.onOpenTab, onCloseAllTabs: props.onCloseAllTabs, onChangeTabs: props.onChangeTabs }))),
        React.createElement(ReorderDragOverlay, { tabs: props.tabs, dragPoint: dragPoint, dragOffset: dragOffset, dragMoveToRect: dragMoveToRect, draggedTab: draggedTab })));
};
const StyledTab = styled(Tab)(() => tabStyle);
const StyledIconButton = styled(IconButton, {
    shouldForwardProp: (propName) => !['fullWidth', 'indicator', 'selectionFollowsFocus', 'textColor'].includes(propName),
})(() => tabStyle);
const Divider = styled(MuiDivider)(() => ({
    alignSelf: 'center',
    height: '24px',
    margin: '0px 8px',
}));
